@if (visible) {
  <div class="studio-backdrop" role="dialog" aria-modal="true" [attr.aria-label]="'imageStudio.title' | transloco">
    <div class="studio">

      <!-- ============================ TOP BAR ============================ -->
      <header class="studio-top">
        <div class="studio-mark">
          <strong>{{ 'imageStudio.title' | transloco }}</strong>
          <span class="studio-artname">{{ productName }}</span>
        </div>

        <!-- Allowance is visible from the first moment, so nothing fails after the fact.
             BOTH windows are shown: an artist with tries left today can still be out
             for the month, and showing only the daily figure would mislead them. -->
        @if (isMetered) {
          <div class="studio-quota" [class.spent-out]="blockedBy !== 'none'"
               [attr.title]="'imageStudio.quotaHint' | transloco">
            <span class="studio-pips" aria-hidden="true">
              @for (i of [].constructor(quota.Daily.Limit); track $index) {
                <span class="studio-pip" [class.spent]="$index < quota.Daily.Used"></span>
              }
            </span>
            <span>{{ 'imageStudio.quotaToday' | transloco: { remaining: quota.Daily.Remaining } }}</span>
            <span class="studio-quota-sep">·</span>
            <span [class.exhausted]="quota.Monthly.Remaining <= 0">{{ 'imageStudio.quotaMonth' | transloco: { remaining: quota.Monthly.Remaining } }}</span>
          </div>
        } @else {
          <div class="studio-quota" [attr.title]="'imageStudio.ownKeyHint' | transloco">
            <span>{{ 'imageStudio.ownKey' | transloco }}</span>
          </div>
        }

        <button type="button" class="studio-close" (click)="close()"
                [attr.aria-label]="'imageStudio.close' | transloco">&times;</button>
      </header>

      @if (errorKey) {
        <p class="studio-error" role="alert">
          {{ errorKey | transloco }}
          @if (errorDetail) { <span class="studio-error-detail">{{ errorDetail }}</span> }
        </p>
      }

      <div class="studio-bench">

        <!-- ============================ STAGE ============================ -->
        <div class="studio-stage">

          @if (screen === 'review' && pendingAsset) {
            <!-- The reveal. Before/after, because seeing the difference IS the product. -->
            <div class="studio-artwrap">
              <div class="studio-compare">
                <img class="studio-cmp-img" [src]="workingAsset?.Url || imageUrl" alt="" />
                <div class="studio-cmp-clip" [style.clip-path]="'inset(0 0 0 ' + comparePercent + '%)'">
                  <img class="studio-cmp-img" [src]="pendingAsset.Url" alt="" />
                </div>
                <span class="studio-cmp-tag left">{{ 'imageStudio.before' | transloco }}</span>
                <span class="studio-cmp-tag right">{{ 'imageStudio.after' | transloco }}</span>
                <input type="range" class="studio-cmp-range" min="0" max="100"
                       [value]="comparePercent"
                       (input)="setComparePercent(+$any($event.target).value)"
                       (keydown)="onCompareKey($event)"
                       [attr.aria-label]="'imageStudio.compareHint' | transloco" />
              </div>
              <p class="studio-caption">{{ 'imageStudio.compareHint' | transloco }}</p>
            </div>
          } @else {
            <div class="studio-artwrap">
              <!-- Square cell + object-fit: contain. Artwork is NEVER cropped or stretched. -->
              <div class="studio-artcell">
                <!-- Everywhere but Versions, the stage shows THE PHOTO THE ARTIST CLICKED.
                     It used to show whatever correction was found for the artwork, which caused
                     two faults at once: the picture visibly swapped a second or two after opening
                     (the fallback resolving into a different asset), and the Adjust preview was
                     built on a corrected image while the server renders the recipe from the
                     ORIGINAL — so preview and result were describing different pictures. The
                     open screen's own caption says "straight from your camera"; now it is. -->
                <img [src]="(screen === 'versions' || screen === 'tools'
                              ? (selectedVersion?.Url || acceptedAsset?.Url || imageUrl)
                              : (original?.Url || imageUrl))"
                     [style.filter]="showsPreview ? previewFilter : null"
                     [style.transform]="showsPreview ? previewTransform : null"
                     (load)="onPreviewImageLoad($event)"
                     alt="" />

                <!-- Resize & reposition (POC). A transparent surface over the picture: drag it to
                     move the photograph, drag a corner to scale it. Only on Adjust, so it cannot
                     swallow clicks meant for the compare divider or the versions list. -->
                @if (screen === 'adjust') {
                  <div class="studio-frame"
                       [class.reframed]="isReframed"
                       (pointerdown)="startFrameDrag($event, 'move')"
                       (pointermove)="onFrameDrag($event)"
                       (pointerup)="endFrameDrag($event)"
                       (pointercancel)="endFrameDrag($event)"
                       (wheel)="onFrameWheel($event)">
                    @for (corner of ['tl', 'tr', 'bl', 'br']; track corner) {
                      <span class="studio-frame-handle" [class]="corner"
                            (pointerdown)="startFrameDrag($event, 'scale')"
                            (pointermove)="onFrameDrag($event)"
                            (pointerup)="endFrameDrag($event)"
                            (pointercancel)="endFrameDrag($event)"></span>
                    }
                  </div>
                }
              </div>
              <p class="studio-caption">
                @switch (screen) {
                  @case ('adjust')  { {{ 'imageStudio.livePreview' | transloco }} }
                  @case ('working') { {{ 'imageStudio.originalSafe' | transloco }} }
                  @default          { {{ 'imageStudio.yourPhoto' | transloco }} }
                }
              </p>
            </div>
          }
        </div>

        <!-- ============================ PANEL ============================ -->
        <div class="studio-panel">

          <!-- ---------- 0 · PREPARING: adopting the existing photo as the Original ---------- -->
          @if (isPreparing) {
            <p class="studio-eyebrow">{{ 'imageStudio.usuallySeconds' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.preparing' | transloco }}</h2>
            <p class="studio-lede">{{ 'imageStudio.preparingDesc' | transloco }}</p>
            <div class="studio-progress"><i></i></div>
          }

          <!-- ---------- 1 · OPEN: outcomes, not tools ---------- -->
          @if (screen === 'open' && !isPreparing) {
            <p class="studio-eyebrow">{{ 'imageStudio.straightFromCamera' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.whatShouldItBecome' | transloco }}</h2>

            <div class="studio-choices">
              <button type="button" class="studio-choice" [disabled]="!canGenerate"
                      (click)="generate('Catalogue')">
                <span class="studio-choice-t">{{ 'imageStudio.catalogueTitle' | transloco }}</span>
                <span class="studio-choice-d">{{ 'imageStudio.catalogueDesc' | transloco }}</span>
                <span class="studio-cost">{{ 'imageStudio.usesOne' | transloco }}</span>
              </button>

              <!-- Unlike a catalogue image this needs a setting first, so it opens a screen
                   rather than starting a job. Asking afterwards would mean charging the artist
                   for a room they never chose. -->
              <button type="button" class="studio-choice" [disabled]="!canGenerate"
                      (click)="startScene()">
                <span class="studio-choice-t">{{ 'imageStudio.sceneTitle' | transloco }}</span>
                <span class="studio-choice-d">{{ 'imageStudio.sceneDesc' | transloco }}</span>
                <span class="studio-cost">{{ 'imageStudio.usesOne' | transloco }}</span>
              </button>
            </div>

            <p class="studio-divider"><span>{{ 'imageStudio.or' | transloco }}</span></p>

            <button type="button" class="studio-btn secondary" (click)="go('adjust')">
              {{ 'imageStudio.adjustMyself' | transloco }}
            </button>

            <p class="studio-note">{{ 'imageStudio.correctionFreeNote' | transloco }}</p>

            <!-- Always offered, even when the original is the only version. "Nothing is
                 ever overwritten" is the promise this screen makes; hiding the proof of
                 it until a second version exists undercuts exactly the reassurance the
                 artist needs BEFORE they let anything touch their work. It is also the
                 only route to the tools. -->
            @if (visibleVersions.length) {
              <button type="button" class="studio-btn quiet" (click)="go('versions')">
                {{ 'imageStudio.seeVersions' | transloco }}
              </button>
            }
          }

          <!-- ---------- 2 · ADJUST: free, and the smart first step ---------- -->
          @if (screen === 'adjust') {
            <p class="studio-eyebrow">{{ 'imageStudio.stepFree' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.fixThePhotograph' | transloco }}</h2>
            <p class="studio-lede">{{ 'imageStudio.fixThePhotographDesc' | transloco }}</p>

            <div class="studio-auto">
              <span>{{ 'imageStudio.weCanSetThese' | transloco }}</span>
              <button type="button" class="studio-btn primary compact" (click)="applyAuto()">
                {{ 'imageStudio.auto' | transloco }}
              </button>
            </div>

            <!-- Straighten (below) only rotates. A flat work photographed at an angle
                 needs its corners rectified, and this is the screen the artist is
                 already on to "fix the photograph" — so it belongs here, not behind
                 the tools drawer. -->
            <div class="studio-auto">
              <span>{{ 'imageStudio.angledPrompt' | transloco }}</span>
              <button type="button" class="studio-btn secondary compact" (click)="useTool('perspective')">
                {{ 'imageStudio.squareUpAction' | transloco }}
              </button>
            </div>

            <div class="studio-sliders">
              @for (s of sliders; track s.key) {
                <div class="studio-slider">
                  <label [attr.for]="'sl-' + s.key">
                    {{ s.labelKey | transloco }}
                    <b>{{ sliderValue(s) | number: '1.0-2' }}{{ s.unit }}</b>
                  </label>
                  <input type="range" [id]="'sl-' + s.key"
                         [min]="s.min" [max]="s.max" [step]="s.step"
                         [value]="sliderValue(s)"
                         (input)="setSlider(s, $any($event.target).value)" />
                </div>
              }

              <!-- Resize & reposition (POC): reads as one more control, because that is what it is. -->
              <p class="studio-frame-hint">
                {{ 'imageStudio.framingHint' | transloco }}
                @if (isReframed) {
                  <button type="button" class="studio-linkbtn" (click)="resetFraming()">
                    {{ 'imageStudio.framingReset' | transloco }}
                  </button>
                }
              </p>

              @if (isReframed && croppedLongestEdge) {
                <p class="studio-frame-size" [class.warn]="isBelowCatalogueStandard">
                  {{ 'imageStudio.framingKeeps' | transloco: { px: croppedLongestEdge } }}
                  @if (isBelowCatalogueStandard) {
                    <span>{{ 'imageStudio.framingBelowStandard' | transloco }}</span>
                  }
                </p>
              }
            </div>

            <!-- Named here, while the artist still knows why they are making it. The version does
                 not exist yet; the name is put onto it the moment the render lands. -->
            <div class="studio-namefield">
              <label for="sl-name">{{ 'imageStudio.nameThisVersion' | transloco }}</label>
              <input type="text" id="sl-name" [(ngModel)]="pendingLabel"
                     [attr.maxlength]="LABEL_MAX"
                     [attr.placeholder]="'imageStudio.nameThisVersionHint' | transloco" />
            </div>

            <div class="studio-row">
              <button type="button" class="studio-btn secondary" (click)="go('open')">
                {{ 'imageStudio.back' | transloco }}
              </button>
              <button type="button" class="studio-btn primary" (click)="applyAdjustments()">
                {{ 'imageStudio.apply' | transloco }}
              </button>
            </div>

            @if (hasAdjustments) {
              <button type="button" class="studio-btn quiet" (click)="resetAdjustments()">
                {{ 'imageStudio.resetAll' | transloco }}
              </button>
            }

            <p class="studio-note">{{ 'imageStudio.previewNote' | transloco }}</p>
          }

          <!-- ---------- 2b · SCENE: the one outcome that needs a decision first ---------- -->
          @if (screen === 'scene') {
            <p class="studio-eyebrow">{{ 'imageStudio.sceneStep' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.sceneHeading' | transloco }}</h2>
            <p class="studio-lede">{{ 'imageStudio.sceneLede' | transloco }}</p>

            <div class="studio-settings" role="radiogroup"
                 [attr.aria-label]="'imageStudio.sceneHeading' | transloco">
              @for (s of sceneSettings; track s.id) {
                <button type="button" class="studio-setting"
                        role="radio"
                        [class.chosen]="sceneSettingId === s.id"
                        [attr.aria-checked]="sceneSettingId === s.id"
                        (click)="chooseSceneSetting(s)">
                  <span class="studio-setting-t">{{ s.labelKey | transloco }}</span>
                  <span class="studio-setting-d">{{ s.descKey | transloco }}</span>
                </button>
              }
            </div>

            <label class="studio-field">
              <span class="studio-field-l">{{ 'imageStudio.sceneDetailLabel' | transloco }}</span>
              <textarea class="studio-textarea" rows="3"
                        [(ngModel)]="sceneDetail"
                        [attr.maxlength]="SCENE_DETAIL_MAX"
                        [attr.placeholder]="'imageStudio.sceneDetailPlaceholder' | transloco"></textarea>
              <span class="studio-field-m">
                {{ 'imageStudio.sceneDetailCount' | transloco: { used: sceneDetailUsed, max: SCENE_DETAIL_MAX } }}
              </span>
            </label>

            <!-- Said BEFORE the artist spends an enhancement, not after: "this shapes the room,
                 not your work" is the reassurance that makes the feature safe to try. -->
            <p class="studio-note">{{ 'imageStudio.sceneDetailHint' | transloco }}</p>

            <div class="studio-row">
              <button type="button" class="studio-btn secondary" (click)="go('open')">
                {{ 'imageStudio.back' | transloco }}
              </button>
              <button type="button" class="studio-btn primary" [disabled]="!canGenerate"
                      (click)="createScene()">
                {{ 'imageStudio.sceneCreate' | transloco }}
              </button>
            </div>

            <p class="studio-note">{{ 'imageStudio.sceneMarketingNote' | transloco }}</p>
          }

          <!-- ---------- 3 · WORKING: honest, and leaveable ---------- -->
          @if (screen === 'working') {
            <p class="studio-eyebrow">{{ 'imageStudio.usuallySeconds' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.workingTitle' | transloco }}</h2>
            <p class="studio-lede">{{ 'imageStudio.workingDesc' | transloco }}</p>

            <div class="studio-progress" role="progressbar"
                 [attr.aria-label]="'imageStudio.workingTitle' | transloco"><i></i></div>

            <ol class="studio-tasks">
              <li class="done">{{ 'imageStudio.taskOriginalSaved' | transloco }}</li>
              <li class="done">{{ 'imageStudio.taskCorrected' | transloco }}</li>
              <li class="now">{{ 'imageStudio.taskCreating' | transloco }}</li>
              <li>{{ 'imageStudio.taskReady' | transloco }}</li>
            </ol>

            <button type="button" class="studio-btn quiet" (click)="cancelJob()">
              {{ 'imageStudio.cancelNothingUsed' | transloco }}
            </button>
          }

          <!-- ---------- 4 · REVIEW: the consent moment ---------- -->
          @if (screen === 'review') {
            <p class="studio-eyebrow">{{ 'imageStudio.nothingSavedYet' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.howDoesThatLook' | transloco }}</h2>
            <p class="studio-lede">{{ 'imageStudio.dragToCompare' | transloco }}</p>

            <button type="button" class="studio-btn primary" [disabled]="isBusy" (click)="keepResult()">
              {{ 'imageStudio.useThisImage' | transloco }}
            </button>
            <button type="button" class="studio-btn secondary" [disabled]="isBusy || !canGenerate"
                    (click)="tryAgain()">
              {{ 'imageStudio.tryAgain' | transloco }}
            </button>
            <button type="button" class="studio-btn quiet" [disabled]="isBusy" (click)="discardResult()">
              {{ 'imageStudio.discard' | transloco }}
            </button>

            <p class="studio-note">{{ 'imageStudio.originalKeptEitherWay' | transloco }}</p>
          }

          <!-- ---------- 5 · VERSIONS: non-destructive, made visible ---------- -->
          @if (screen === 'versions') {
            <p class="studio-eyebrow">{{ 'imageStudio.neverOverwritten' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.versions' | transloco }}</h2>
            <p class="studio-lede">{{ 'imageStudio.versionsDesc' | transloco }}</p>

            <div class="studio-versions">
              @for (a of visibleVersions; track trackByAssetId($index, a)) {
                <!-- "In use" is IsCurrent, never Status: originals and corrections are
                     both Accepted, so Status would badge several rows at once. -->
                <!-- Clicking a row shows it in the big view. The screen's whole job is choosing
                     between versions, and a stamp-sized thumbnail is not enough to choose on. -->
                <div class="studio-version" [class.current]="a.IsCurrent"
                     [class.viewing]="selectedVersionId === a.Id"
                     (click)="selectVersion(a)">
                  <span class="studio-version-sw">
                    <img [src]="a.Url" alt="" />
                  </span>
                  <span class="studio-version-txt">
                    <span class="t">{{ versionLabelKey(a) | transloco }}</span>
                    <!-- A lazily-seeded Original is stored without dimensions; showing
                         "0×0" reads as a broken file rather than an unmeasured one. -->
                    <span class="m">{{ a.CreatedDate | date: 'd MMM, HH:mm' }}@if (a.Width && a.Height) { · {{ a.Width }}×{{ a.Height }} }</span>

                    <!-- The artist's note. Arrives pre-filled describing what changed, so the
                         six-month question has an answer even when nobody types anything. -->
                    @if (editingLabelFor === a.Id) {
                      <input type="text" class="studio-version-note-edit" [(ngModel)]="labelDraft"
                             [attr.maxlength]="LABEL_MAX" [attr.aria-label]="'imageStudio.noteAria' | transloco"
                             (keyup.enter)="saveLabel(a)" (keyup.escape)="cancelLabel()" (blur)="saveLabel(a)" />
                    } @else {
                      <button type="button" class="studio-version-note" (click)="editLabel(a)"
                              [attr.title]="'imageStudio.noteEdit' | transloco">
                        {{ a.Label || ('imageStudio.noteAdd' | transloco) }}
                      </button>
                    }
                  </span>

                  @if (a.IsCurrent) {
                    <span class="studio-badge live">{{ 'imageStudio.mainBadge' | transloco }}</span>
                  } @else if (isMarketing(a)) {
                    <!-- Badged rather than left to look like a version that simply lost: it is not
                         a candidate for the main image and never will be. -->
                    <span class="studio-badge marketing">{{ 'imageStudio.marketingBadge' | transloco }}</span>
                  } @else if (a.Kind === 'Original') {
                    <span class="studio-badge orig">{{ 'imageStudio.originalBadge' | transloco }}</span>
                  }

                  <span class="studio-version-actions">
                    <!-- A product shot's frame contains a room, so it can never be the artwork's
                         main picture. The server refuses it too — this stops us offering a button
                         that could only fail. -->
                    @if (canSetMain(a)) {
                      <button type="button" class="studio-link" [disabled]="isBusy" (click)="setMain(a)">
                        {{ 'imageStudio.setMain' | transloco }}
                      </button>
                    }
                    <!-- Corrections only: a generation cost money and cannot be re-derived, so
                         removing one is a different decision from discarding a free adjustment. -->
                    @if (canDelete(a)) {
                      <button type="button" class="studio-link danger" [disabled]="isBusy" (click)="deleteVersion(a)">
                        {{ 'imageStudio.deleteVersion' | transloco }}
                      </button>
                    }
                  </span>
                </div>
              }
            </div>

            <p class="studio-note">{{ 'imageStudio.retroactiveNote' | transloco }}</p>

            <!-- Done/Cancel replace "Back", which said nothing about what it had done.
                 Everything here is ALREADY saved — the only uncommitted decision is which
                 version is main, so that is exactly what these two commit or abandon. -->
            <button type="button" class="studio-btn primary" [disabled]="isBusy" (click)="finish()">
              {{ 'imageStudio.done' | transloco }}
            </button>
            <button type="button" class="studio-btn quiet" [disabled]="isBusy" (click)="cancelStudio()">
              {{ 'imageStudio.cancel' | transloco }}
            </button>
          }

          <!-- ---------- 6 · TOOLS ----------
               Was "More things I can do": four cards, three of them disabled with a note
               saying the rest were coming. A menu of unbuilt futures is not a feature, it is
               a promise the artist cannot act on — and it read as confusion rather than
               capability. Only the action that WORKS remains, offered on the screen where it
               is relevant instead of behind a submenu. Bring the others back as they land. -->
          @if (screen === 'tools') {
            <p class="studio-eyebrow">{{ 'imageStudio.whenYouWantControl' | transloco }}</p>
            <h2 class="studio-h">{{ 'imageStudio.toolSquareUp' | transloco }}</h2>
            <p class="studio-lede">{{ 'imageStudio.toolSquareUpDesc' | transloco }}</p>

            <!-- For a flat work this is the FAITHFUL route: the artwork is measured and
                 rectified, never redrawn — so unlike the AI outcomes it cannot alter the
                 proportions of the piece. -->
            <button type="button" class="studio-btn primary" (click)="useTool('perspective')">
              {{ 'imageStudio.toolSquareUpAction' | transloco }}
            </button>
            <button type="button" class="studio-btn quiet" (click)="go('versions')">
              {{ 'imageStudio.backToVersions' | transloco }}
            </button>
          }
        </div>
      </div>
    </div>
  </div>
}
